import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import ErrorComponent from '../../components/error/error';
import Form from '../../components/form/form';
import Loading from '../../components/loading/loading';
import StudentSelector from '../../components/student-selector/student-selector';
import SubmitButton from '../../components/submit-button/submit-button';

// Form Data
import { formDataDiaryCreate } from '../../form-data/form-data-diary-create';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import DiaryService from '../../services/diary-service';
import SchoolService from '../../services/school-service';
import StorageService from '../../services/storage-service';

// Helpers
import {
  SELECTOR_CLASSROOM_ID,
  draftedStudentUuids,
  dayTakenMessage,
  formDataToContent,
  selectedStudents,
  staffNamesOf,
  toSelectorClassroom,
} from '../../helpers/daily-records-helper';

const DiaryFormCreate = () => {
  const { classroomUuid } = useParams<{ classroomUuid: string }>();
  const navigate = useNavigate();
  const { setToastContent } = useContext(GeneralContext);
  const [error, setError] = useState(false);
  const [diaryService, setDiaryService] = useState<DiaryService | null>(null);
  const [todayGroups, setTodayGroups] = useState<ClassroomDiaryGroup[]>([]);

  const initialData: DiaryFormData = {
    formData: {
      breakfast: null,
      lunch: null,
      afternoonCoffee: null,
      dinner: null,
      sleep: null,
      evacuate: null,
      shower: null,
      diaper: null,
      details: null,
    },
    classData: [],
    studentData: [],
  };
  const [data, setData] = useState<DiaryFormData>(initialData);

  const [validations, setValidations] = useState({});
  const [allowSubmit, setAllowSubmit] = useState(false);
  useEffect(() => {
    const allDetailedFormFilled =
      (
        classroomList &&
        classroomList.length > 0 &&
        classroomList[0].diaryMode === 'Preschool'
      )
      &&
      (
        data.formData.breakfast ||
        data.formData.lunch ||
        data.formData.afternoonCoffee ||
        data.formData.dinner ||
        (data.formData.sleep !== null && data.formData.sleep >= 0) ||
        (data.formData.evacuate !== null && data.formData.evacuate >= 0) ||
        (data.formData.shower !== null && data.formData.shower >= 0) ||
        (data.formData.diaper !== null && data.formData.diaper >= 0) ||
        data.formData.details
      );

    const allSimpleFormFilled =
      classroomList &&
      classroomList.length > 0 &&
      classroomList[0].diaryMode === 'Simple' &&
      data.formData.details;

    if (
      (allDetailedFormFilled || allSimpleFormFilled) &&
      data.studentData.length > 0 &&
      Object.keys(validations).length === 0
    ) {
      setAllowSubmit(true);
    } else if (allowSubmit) {
      setAllowSubmit(false);
    }
  }, [validations, allowSubmit, data.studentData]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialTitle = 'Selecione um aluno';
  const [title, setTitle] = useState(initialTitle);

  const handleFormOnChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const id = e.target.id;
    let value = e.target.value;
    if (id !== 'details' && value === 'default') {
      value = null;
    }

    const parsedValue = parseInt(value);
    const fieldValue = isNaN(parsedValue) ? value : parsedValue;

    // Parte do estado anterior, não do `data` deste render: o StudentSelector avisa a seleção num efeito, então uma
    // digitação logo depois do toque pode rodar antes de essa atualização renderizar, e uma cópia de `data` perderia o aluno.
    setValidations((previous) => {
      const next = { ...previous };
      if (!value && classroomList[0].diaryMode === 'Simple' && id === 'details')
        next[id] = 'Este campo deve ser preenchido';
      else delete next[id];
      return next;
    });
    setData((previous) => ({ ...previous, formData: { ...previous.formData, [id]: fieldValue } }));
  };

  const [formData, setFormData] = useState(null);
  const [classroomList, setClassroomList] = useState<
    ClassroomStudents[] | null
  >(null);
  useEffect(() => {
    new SchoolService()
      .getSchoolById(new StorageService().getEmployee().schoolId)
      .then((school) => {
        const service = new DiaryService(school.uuid);
        setDiaryService(service);

        return Promise.all([service.listPendingStudents(classroomUuid), service.listDiaryGroups(classroomUuid)]);
      })
      .then(([pendingWithDrafted, groups]) => {
        // Um aluno em rascunho continua pendente, mas o dia dele já está ocupado: continua-se o rascunho.
        const drafted = draftedStudentUuids(groups);
        const pending = {
          ...pendingWithDrafted,
          students: pendingWithDrafted.students.filter((student) => !drafted.has(student.uuid)),
        };
        setTodayGroups(groups);

        if (pending.students.length === 0) {
          navigate(`/diarios/turma/${classroomUuid}`);
          return;
        }

        setFormData(formDataDiaryCreate(pending.diaryMode));
        setClassroomList([toSelectorClassroom(pending)]);
      })
      .catch(() => setError(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStudentChange = (
    selectedClassrooms: number[],
    selectedStudents: number[]
  ) => {
    const studentNames: { id: number; name: string }[] = [];
    classroomList.forEach((classroom) => {
      classroom.students.forEach((student) => {
        if (selectedStudents.includes(student.id))
          studentNames.push({ id: student.id, name: student.name });
      });
    });

    let newTitle = '';
    if (studentNames.length > 0) {
      if (studentNames.length === 1) {
        newTitle = `${studentNames[0].name} hoje:`;
      } else if (studentNames.length === 2) {
        newTitle = `${studentNames[0].name} e ${studentNames[1].name} hoje:`;
      } else if (studentNames.length === 3) {
        newTitle = `${studentNames[0].name}, ${studentNames[1].name} e ${studentNames[2].name} hoje:`;
      } else {
        newTitle = `${studentNames[0].name}, e mais ${studentNames.length - 1} alunos hoje:`;
      }
    } else {
      newTitle = initialTitle;
    }

    setTitle(newTitle);
    setData((previous) => ({ ...previous, classData: selectedClassrooms, studentData: selectedStudents }));
  };

  const handleSubmit = (status: DiaryGroupStatus) => {
    const students = selectedStudents(classroomList[0], data.studentData);
    const content = formDataToContent(data.formData, classroomList[0].diaryMode);

    diaryService
      .createDiaryGroup({ classroomUuid, studentUuids: students.map((student) => student.uuid), content, status })
      .then(() => {
        setToastContent({
          isOpen: true,
          text: status === 'Draft' ? 'Rascunho salvo!' : 'Diário preenchido!',
          type: 'success',
        });
        navigate(`/diarios/turma/${classroomUuid}`);
      })
      .catch((err) => {
        const studentNames = Object.fromEntries(students.map((student) => [student.uuid, student.name]));
        setToastContent({
          isOpen: true,
          text:
            dayTakenMessage(err, studentNames, staffNamesOf(todayGroups)) ||
            (status === 'Draft' ? 'Houve um erro ao salvar o rascunho.' : 'Houve um erro ao preencher o diário.'),
          type: 'error',
        });
      });
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!classroomList) return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.doubleWithActionButtons}
      biggerSide={BaseContentContainerBiggerSide.right}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <Form
        formData={formData}
        validations={validations}
        handleOnChange={handleFormOnChange}
        title={title}
      />
      {classroomList && (
        <StudentSelector
          classrooms={classroomList}
          handleStudentChange={handleStudentChange}
          lockClassroomSelection={true}
          initialClassrooms={[SELECTOR_CLASSROOM_ID]}
        />
      )}
      <ButtonsBar pattern='together' sideAlign='right' verticalAlign='bottom'>
        <Link to={`/diarios/turma/${classroomUuid}`}>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        {/* Um rascunho pode estar incompleto: basta ter alunos. */}
        <SubmitButton
          value='salvar rascunho'
          variant='secondary'
          onClick={() => handleSubmit('Draft')}
          disabled={data.studentData.length === 0}
        />
        <SubmitButton
          value='enviar'
          variant='primary'
          onClick={() => handleSubmit('Published')}
          disabled={!allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default DiaryFormCreate;
